import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Lock, Wand2, Loader2, Send, Palette,
  ChevronDown, ChevronUp, ExternalLink, LayoutDashboard,
  RefreshCw, Monitor, Smartphone,
} from "lucide-react";
import { api, AUTH, BUILD_SESSION } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// ── Palette presets ─────────────────────────────────────────────────────────
const PALETTES = [
  { id: "burnt_sienna",  name: "Burnt sienna",  primary: "#d4724a", secondary: "#f5a572", background: "#fffbf5", foreground: "#1a1a1a" },
  { id: "midnight_ink",  name: "Midnight ink",   primary: "#d4a76a", secondary: "#8b6f47", background: "#0f0f0f", foreground: "#f5f5f5" },
  { id: "forest_cream",  name: "Forest & cream", primary: "#2d5a3d", secondary: "#4a7a5a", background: "#f5f9f5", foreground: "#1a1a1a" },
  { id: "ocean_slate",   name: "Ocean slate",    primary: "#2b6f9f", secondary: "#4a8fb5", background: "#f0f5f9", foreground: "#1a2a3a" },
  { id: "deep_rouge",    name: "Deep rouge",     primary: "#8b3a3a", secondary: "#c96b6b", background: "#faf6f6", foreground: "#1a1a1a" },
  { id: "obsidian_lime", name: "Obsidian & lime",primary: "#c8ff00", secondary: "#a8d900", background: "#0a0a0a", foreground: "#f5f5f5" },
  { id: "soft_blossom",  name: "Soft blossom",   primary: "#d97a91", secondary: "#f5b6cc", background: "#fef9f7", foreground: "#1a1a1a" },
  { id: "pure_minimal",  name: "Pure minimal",   primary: "#1a1a1a", secondary: "#333333", background: "#ffffff", foreground: "#1a1a1a" },
];

function paletteInstruction(p) {
  return `Switch the color palette to "${p.name}" — set primary to ${p.primary}, secondary to ${p.secondary}, background to ${p.background}, foreground/text to ${p.foreground}. Keep everything else the same.`;
}

// ── Credit gate modal ────────────────────────────────────────────────────────
function CreditGateModal({ slug, onClose }) {
  const [loading, setLoading] = useState(false);
  const topup = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/tenants/${slug}/iterate-topup`, {}, { headers: { origin: window.location.origin } });
      window.location.href = r.data.url;
    } catch {
      toast.error("Couldn't start checkout — try again");
      setLoading(false);
    }
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"#fff", borderRadius:16, padding:"36px 32px", maxWidth:440, width:"100%", boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize:36, marginBottom:12 }}>✦</div>
        <h2 style={{ fontSize:22, fontWeight:700, marginBottom:8 }}>Ready for more?</h2>
        <p style={{ color:"#555", fontSize:14, lineHeight:1.6, marginBottom:20 }}>
          You've used your free refinement. Add <strong>$5 for 10 more prompts</strong> — that's plenty to get it exactly right.
        </p>
        <div style={{ display:"flex", gap:10 }}>
          <Button onClick={topup} disabled={loading} style={{ flex:1 }}>
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2"/>}
            Add 10 prompts — $5
          </Button>
          <Button variant="outline" onClick={onClose}>Not now</Button>
        </div>
      </div>
    </div>
  );
}

// ── Swatch ──────────────────────────────────────────────────────────────────
function Swatch({ color }) {
  return <div style={{ width:18, height:18, borderRadius:3, background:color, border:"1px solid rgba(0,0,0,0.1)", flexShrink:0 }}/>;
}

// ── Main component ───────────────────────────────────────────────────────────
export default function Preview() {
  const { slug } = useParams();
  const nav = useNavigate();
  const iframeRef = useRef(null);
  const textareaRef = useRef(null);

  const [tenant, setTenant] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [iterateCount, setIterateCount] = useState(0);
  const [iterateCredits, setIterateCredits] = useState(0);
  const [showCreditGate, setShowCreditGate] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [selectedPaletteId, setSelectedPaletteId] = useState(null);
  const [iframeViewport, setIframeViewport] = useState("desktop"); // desktop | mobile
  const [iframeKey, setIframeKey] = useState(0); // force reload iframe

  // /s/:slug = SpecStorefront (ClassicArchetype) — full landing page with hero,
  // featured items, story, and the merchant's AI-generated theme applied.
  // /t/:slug is the legacy menu-only view; do not use in the preview panel.
  const storefrontUrl = `${window.location.origin}/s/${slug}`;

  const load = useCallback(async () => {
    try {
      const t = await api.get(`/tenants/${slug}`);
      setTenant(t.data);
      setIterateCount(t.data.iterate_count ?? 0);
      setIterateCredits(t.data.iterate_credits ?? 0);
      BUILD_SESSION.set(slug, t.data.name);
    } catch {
      toast.error("Couldn't load storefront");
    }
  }, [slug]);

  useEffect(() => {
    load();
    if (window.location.search.includes("topup=success")) {
      toast.success("Credits added! You have 10 more refinements.");
    }
  }, [load]);

  const refreshIframe = () => setIframeKey(k => k + 1);


  const applyInstruction = async (instruction) => {
    if (!instruction.trim()) return;
    if (iterateCount >= 1 && iterateCredits <= 0) { setShowCreditGate(true); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${slug}/iterate`, { instruction });
      setHistory(h => [{ instruction, ts: new Date().toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }) }, ...h]);
      setPrompt("");
      setSelectedPaletteId(null);
      if (r.data.iterate_count !== undefined) setIterateCount(r.data.iterate_count);
      if (r.data.iterate_credits !== undefined) setIterateCredits(r.data.iterate_credits);
      await load();
      refreshIframe();
      toast.success("Applied ✓");
    } catch (e) {
      if (e?.response?.status === 402) setShowCreditGate(true);
      else toast.error(e?.response?.data?.detail || "Couldn't apply that change");
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) applyInstruction(prompt);
  };

  const signupNext = `/signup?next=${encodeURIComponent(`/preview/${slug}`)}`;

  const unlock = async () => {
    if (!AUTH.token()) { toast.error("Sign up first to unlock"); nav(signupNext); return; }
    const r = await api.post(`/tenants/${slug}/unlock-preview`, {}, { headers: { origin: window.location.origin } });
    window.location.href = r.data.url;
  };

  const claim = async () => {
    if (!AUTH.token()) { nav(signupNext); return; }
    try {
      await api.post(`/tenants/${slug}/claim`);
      toast.success("Claimed");
      load();
    } catch (e) {
      if (e?.response?.status === 401) {
        // Stale/expired token — clear it and restart the signup flow with a return path.
        AUTH.clear();
        nav(signupNext);
        return;
      }
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!tenant) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", fontSize:14, color:"#888" }}>
        Loading preview…
      </div>
    );
  }

  const creditsRemaining = iterateCredits > 0 ? iterateCredits : (iterateCount === 0 ? "1 free" : 0);
  const creditsLabel = iterateCount === 0 ? "1 free prompt" : iterateCredits > 0 ? `${iterateCredits} prompts left` : "0 prompts left";
  const creditsColor = iterateCount === 0 ? "#2d7a3a" : iterateCredits > 0 ? "#2d7a3a" : "#b06000";
  const creditsBg    = iterateCount === 0 ? "#f0faf0" : iterateCredits > 0 ? "#f0faf0" : "#fff8f0";

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh", overflow:"hidden", fontFamily:"inherit" }}>
      {showCreditGate && <CreditGateModal slug={slug} onClose={() => setShowCreditGate(false)}/>}

      {/* ── Top bar ── */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"0 16px", height:48, background:"#0f0f0f", flexShrink:0,
        borderBottom:"1px solid #2a2a2a",
      }}>
        {/* Left: brand + tenant name */}
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontFamily:"var(--font-script, serif)", fontSize:20, color:"#fff", lineHeight:1 }}>ordrs</span>
          <div style={{ width:1, height:20, background:"#333" }}/>
          <span style={{ fontSize:12, color:"#888", fontWeight:500 }}>{tenant.name}</span>
          <div style={{
            fontSize:10, fontWeight:700, letterSpacing:"0.08em", padding:"2px 8px",
            background:"rgba(255,255,255,0.08)", borderRadius:20, color:"#aaa",
            textTransform:"uppercase",
          }}>
            LIVE PREVIEW
          </div>
        </div>

        {/* Center: viewport toggle */}
        <div style={{ display:"flex", gap:4, background:"#1a1a1a", borderRadius:8, padding:3 }}>
          {[["desktop", Monitor], ["mobile", Smartphone]].map(([v, Icon]) => (
            <button
              key={v}
              onClick={() => setIframeViewport(v)}
              style={{
                display:"flex", alignItems:"center", justifyContent:"center",
                width:28, height:28, borderRadius:6, border:"none",
                background: iframeViewport === v ? "#fff" : "transparent",
                color: iframeViewport === v ? "#0f0f0f" : "#666",
                cursor:"pointer", transition:"all 100ms",
              }}
            >
              <Icon size={14}/>
            </button>
          ))}
        </div>

        {/* Right: actions */}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <button onClick={refreshIframe} title="Refresh preview" style={{ background:"none", border:"none", cursor:"pointer", color:"#666", display:"flex", alignItems:"center", padding:6 }}>
            <RefreshCw size={14}/>
          </button>
          <a href={storefrontUrl} target="_blank" rel="noopener noreferrer" style={{ color:"#666", display:"flex", alignItems:"center", padding:6, textDecoration:"none" }} title="Open in new tab">
            <ExternalLink size={14}/>
          </a>
          {!tenant.owner_user_id && (
            <button onClick={claim} title="Save this storefront to a free account" style={{ fontSize:12, padding:"5px 12px", borderRadius:6, border:"1px solid #444", background:"transparent", color:"#ccc", cursor:"pointer" }}>
              {AUTH.token() ? "Claim" : "Save free"}
            </button>
          )}
          <button onClick={unlock} style={{ fontSize:12, padding:"5px 12px", borderRadius:6, border:"1px solid #444", background:"transparent", color:"#ccc", cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
            <Lock size={11}/>Unlock ($10)
          </button>
          <Link to={`/onboard/${slug}`}>
            <button style={{ fontSize:12, padding:"5px 14px", borderRadius:6, border:"none", background:"#d4724a", color:"#fff", cursor:"pointer", fontWeight:600, display:"flex", alignItems:"center", gap:5 }}>
              Activate <ArrowRight size={12}/>
            </button>
          </Link>
        </div>
      </div>

      {/* ── Split body ── */}
      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>

        {/* ── LEFT PANEL — agent ── */}
        <div style={{
          width:340, flexShrink:0, display:"flex", flexDirection:"column",
          borderRight:"1px solid #e8e8e8", background:"#fafaf9", overflow:"hidden",
        }}>
          {/* Panel header */}
          <div style={{ padding:"14px 16px 10px", borderBottom:"1px solid #ebebeb" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
              <Wand2 size={13} color="#d4724a"/>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"#888" }}>AI Builder</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:13, fontWeight:600, color:"#1a1a1a" }}>Describe what you want</span>
              <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:20, background:creditsBg, color:creditsColor, border:`1px solid ${creditsColor}33` }}>
                {creditsLabel}
              </span>
            </div>
          </div>

          {/* Prompt box */}
          <div style={{ padding:"12px 14px", borderBottom:"1px solid #ebebeb" }}>
            {iterateCount === 0 && (
              <div style={{ fontSize:11, color:"#888", lineHeight:1.5, marginBottom:10, padding:"8px 10px", background:"#fff", border:"1px solid #e8e3dc", borderRadius:7 }}>
                <strong style={{ color:"#555" }}>First refinement is free.</strong> Describe the whole vibe shift — colors, copy, items, feel. Most storefronts land in 2–3 prompts.
              </div>
            )}
            <div style={{ position:"relative" }}>
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={5}
                placeholder={"e.g. \"Make it feel like a cozy Italian trattoria — warm terracotta tones, serif headings. Add a truffle pasta at $22.\"\n\nor: \"Go darker and more luxurious\"\nor: \"Modern Japanese minimalist\""}
                style={{
                  width:"100%", padding:"10px 36px 10px 10px",
                  border:"1.5px solid #ddd", borderRadius:8,
                  fontSize:12.5, lineHeight:1.55, resize:"none",
                  fontFamily:"inherit", outline:"none", background:"#fff",
                  transition:"border-color 120ms", boxSizing:"border-box",
                  color:"#1a1a1a",
                }}
                onFocus={e => e.target.style.borderColor="#d4724a"}
                onBlur={e => e.target.style.borderColor="#ddd"}
              />
              <button
                onClick={() => applyInstruction(prompt)}
                disabled={busy || !prompt.trim()}
                title="Apply (Cmd+Enter)"
                style={{
                  position:"absolute", right:7, bottom:8,
                  width:28, height:28, borderRadius:6,
                  background: busy || !prompt.trim() ? "#e8e8e8" : "#d4724a",
                  border:"none", cursor: busy || !prompt.trim() ? "not-allowed" : "pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  transition:"background 120ms",
                }}
              >
                {busy
                  ? <Loader2 size={13} color="#999" style={{ animation:"spin 1s linear infinite" }}/>
                  : <Send size={12} color={busy || !prompt.trim() ? "#bbb" : "#fff"}/>
                }
              </button>
            </div>
            <div style={{ marginTop:6, fontSize:10.5, color:"#aaa" }}>Cmd+Enter to apply</div>
          </div>

          {/* Palette picker */}
          <div style={{ borderBottom:"1px solid #ebebeb" }}>
            <button
              onClick={() => setPaletteOpen(o => !o)}
              style={{
                width:"100%", padding:"10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between",
                background:"none", border:"none", cursor:"pointer", fontSize:12, color:"#555", fontWeight:600,
              }}
            >
              <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                <Palette size={13}/>Quick palette
              </span>
              {paletteOpen ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
            </button>
            {paletteOpen && (
              <div style={{ padding:"0 14px 12px", display:"flex", flexDirection:"column", gap:6 }}>
                {PALETTES.map(p => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setSelectedPaletteId(p.id);
                      setPrompt(paletteInstruction(p));
                      textareaRef.current?.focus();
                    }}
                    style={{
                      display:"flex", alignItems:"center", gap:8,
                      padding:"7px 10px", borderRadius:7, textAlign:"left",
                      background: selectedPaletteId === p.id ? p.background : "#fff",
                      border: selectedPaletteId === p.id ? `2px solid ${p.primary}` : "1.5px solid #eee",
                      cursor:"pointer", transition:"all 80ms",
                    }}
                  >
                    <div style={{ display:"flex", gap:3 }}>
                      {[p.primary, p.background, p.secondary].map((c, i) => <Swatch key={i} color={c}/>)}
                    </div>
                    <span style={{ fontSize:12, fontWeight:600, color:"#1a1a1a" }}>{p.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Change history */}
          <div style={{ flex:1, overflowY:"auto", padding:"10px 14px" }}>
            {history.length === 0 ? (
              <div style={{ fontSize:11, color:"#bbb", textAlign:"center", marginTop:20 }}>
                No changes yet
              </div>
            ) : (
              <div>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"#bbb", marginBottom:8 }}>
                  Change history
                </div>
                {history.map((h, i) => (
                  <div key={i} style={{
                    padding:"8px 10px", marginBottom:6, borderRadius:7,
                    background:"#fff", border:"1px solid #ebebeb", fontSize:11.5,
                  }}>
                    <div style={{ color:"#1a1a1a", lineHeight:1.4, marginBottom:2 }}>"{h.instruction}"</div>
                    <div style={{ color:"#bbb", fontSize:10 }}>{h.ts}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom nav links */}
          <div style={{ padding:"10px 14px", borderTop:"1px solid #ebebeb", display:"flex", gap:6 }}>
            <Link to={`/dashboard/${slug}`} style={{ flex:1 }}>
              <button style={{ width:"100%", padding:"7px 0", fontSize:11, fontWeight:600, borderRadius:7, border:"1.5px solid #e0e0e0", background:"#fff", cursor:"pointer", color:"#555", display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}>
                <LayoutDashboard size={11}/>Dashboard
              </button>
            </Link>
            <a href={storefrontUrl} target="_blank" rel="noopener noreferrer" style={{ flex:1 }}>
              <button style={{ width:"100%", padding:"7px 0", fontSize:11, fontWeight:600, borderRadius:7, border:"1.5px solid #e0e0e0", background:"#fff", cursor:"pointer", color:"#555", display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}>
                <ExternalLink size={11}/>Full page
              </button>
            </a>
          </div>
        </div>

        {/* ── RIGHT PANEL — live storefront iframe ── */}
        <div style={{
          flex:1, display:"flex", flexDirection:"column", background:"#f0efee",
          alignItems:"center", overflow:"hidden",
        }}>
          {/* Mobile frame wrapper */}
          <div style={{
            flex:1, width:"100%", display:"flex", alignItems:"center", justifyContent:"center",
            padding: iframeViewport === "mobile" ? "20px 0" : 0,
            transition:"padding 200ms",
            overflow: "hidden",
          }}>
            <div style={{
              width: iframeViewport === "mobile" ? 390 : "100%",
              height: iframeViewport === "mobile" ? "calc(100% - 40px)" : "100%",
              borderRadius: iframeViewport === "mobile" ? 32 : 0,
              overflow: "hidden",
              boxShadow: iframeViewport === "mobile" ? "0 20px 60px rgba(0,0,0,0.25)" : "none",
              border: iframeViewport === "mobile" ? "8px solid #1a1a1a" : "none",
              transition:"all 200ms",
              flexShrink:0,
            }}>
              <iframe
                key={iframeKey}
                ref={iframeRef}
                src={storefrontUrl}
                style={{ width:"100%", height:"100%", border:"none", display:"block", background:"#fff" }}
                title="Storefront preview"
              />
            </div>
          </div>

          {/* Overlay loading state */}
          {busy && (
            <div style={{
              position:"absolute", inset:0, background:"rgba(240,239,238,0.7)",
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12,
              zIndex:10,
            }}>
              <Loader2 size={28} color="#d4724a" style={{ animation:"spin 1s linear infinite" }}/>
              <span style={{ fontSize:13, color:"#888", fontWeight:500 }}>Applying changes…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
